/**
 * cryptoService.js
 * ─────────────────────────────────────────────────────────────────────────────
 * E2EE crypto primitives for Praysely web.
 *
 * Curve25519 key exchange + XSalsa20-Poly1305 encryption via tweetnacl.
 * window.crypto.getRandomValues is available natively in all modern browsers.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import nacl from 'tweetnacl'
import naclUtil from 'tweetnacl-util'

/** Cryptographically secure random bytes — native in browsers. */
function randomBytes(n) {
  const buf = new Uint8Array(n)
  window.crypto.getRandomValues(buf)
  return buf
}

// ── helpers ──────────────────────────────────────────────────────────────────

export const b64ToBytes = (b64) => naclUtil.decodeBase64(b64)
export const bytesToB64 = (bytes) => naclUtil.encodeBase64(bytes)
const strToBytes  = (s) => naclUtil.decodeUTF8(s)
const bytesToStr  = (b) => naclUtil.encodeUTF8(b)

// ── key pair ─────────────────────────────────────────────────────────────────

/**
 * Generate a new Curve25519 key pair.
 * Returns { publicKey, privateKey } both as base64 strings.
 */
export function generateKeyPair() {
  const secretKey = randomBytes(32)
  const kp = nacl.box.keyPair.fromSecretKey(secretKey)
  return {
    publicKey:  bytesToB64(kp.publicKey),
    privateKey: bytesToB64(kp.secretKey),
  }
}

// ── message encryption ────────────────────────────────────────────────────────

/**
 * Encrypt a plaintext message.
 * Returns { ciphertext, nonce } both as base64 strings.
 */
export function encryptMessage(plaintext, recipientPublicKey, senderPrivateKey) {
  const nonce            = randomBytes(nacl.box.nonceLength)
  const messageBytes     = strToBytes(plaintext)
  const recipientPKBytes = b64ToBytes(recipientPublicKey)
  const senderSKBytes    = b64ToBytes(senderPrivateKey)

  const box = nacl.box(messageBytes, nonce, recipientPKBytes, senderSKBytes)

  return {
    ciphertext: bytesToB64(box),
    nonce:      bytesToB64(nonce),
  }
}

/**
 * Decrypt a message.
 * Works for both sender and recipient (nacl.box uses the same shared secret
 * in both directions).
 * Returns plaintext string, or null if decryption fails.
 */
export function decryptMessage(ciphertext, nonce, theirPublicKey, myPrivateKey) {
  try {
    const cipherBytes  = b64ToBytes(ciphertext)
    const nonceBytes   = b64ToBytes(nonce)
    const theirPKBytes = b64ToBytes(theirPublicKey)
    const mySKBytes    = b64ToBytes(myPrivateKey)

    const decrypted = nacl.box.open(cipherBytes, nonceBytes, theirPKBytes, mySKBytes)
    if (!decrypted) return null
    return bytesToStr(decrypted)
  } catch {
    return null
  }
}

// ── passphrase-based key backup ───────────────────────────────────────────────

const PBKDF_ITERATIONS = 100_000
const KEY_LENGTH       = nacl.secretbox.keyLength  // 32 bytes

/**
 * Derive a 32-byte key from a passphrase + salt using iterative SHA-512.
 */
async function deriveKeyFromPassphrase(passphrase, salt) {
  let input = new Uint8Array([...strToBytes(passphrase), ...salt])
  let hash  = nacl.hash(input)
  for (let i = 1; i < PBKDF_ITERATIONS; i++) {
    hash = nacl.hash(hash)
  }
  return hash.slice(0, KEY_LENGTH)
}

/**
 * Encrypt a private key with a passphrase for cloud backup.
 * Returns { encryptedKey, salt, nonce } all as base64 strings.
 */
export async function encryptPrivateKeyWithPassphrase(privateKeyB64, passphrase) {
  const salt       = randomBytes(32)
  const nonce      = randomBytes(nacl.secretbox.nonceLength)
  const derivedKey = await deriveKeyFromPassphrase(passphrase, salt)
  const keyBytes   = b64ToBytes(privateKeyB64)
  const encrypted  = nacl.secretbox(keyBytes, nonce, derivedKey)

  return {
    encryptedKey: bytesToB64(encrypted),
    salt:         bytesToB64(salt),
    nonce:        bytesToB64(nonce),
  }
}

/**
 * Decrypt a backed-up private key using the user's passphrase.
 * Returns the private key as base64, or null if passphrase is wrong.
 */
export async function decryptPrivateKeyWithPassphrase(backup, passphrase) {
  try {
    const salt       = b64ToBytes(backup.salt)
    const nonce      = b64ToBytes(backup.nonce)
    const encrypted  = b64ToBytes(backup.encryptedKey)
    const derivedKey = await deriveKeyFromPassphrase(passphrase, salt)
    const decrypted  = nacl.secretbox.open(encrypted, nonce, derivedKey)
    if (!decrypted) return null
    return bytesToB64(decrypted)
  } catch {
    return null
  }
}
